'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useFirestore } from '@/firebase/index';
import { collection, query, where, onSnapshot, or, and } from 'firebase/firestore';
import { useFeatureFlags } from '@/hooks/use-feature-flags';

interface MessageNotificationFabProps {
  userId: string;
  isOfficer?: boolean;
  isAdmin?: boolean;
}

/**
 * Floating Action Button (FAB) that shows new message count
 * and navigates to messages page when clicked.
 * Only visible when user has unread messages.
 *
 * Officer/admin behaviour is governed by the Officer Messages
 * feature flag: when the flag is on they see personal messages plus
 * admin-board messages (receiverId === null) and the click target
 * is /admin/messages. When the flag is off the inbox is fully
 * disabled — the FAB falls back to the personal-only query and
 * routes to /messages so it doesn't surface a count that points at
 * a gated route.
 */
export function MessageNotificationFab({ userId, isOfficer, isAdmin }: MessageNotificationFabProps) {
  const router = useRouter();
  const firestore = useFirestore();
  const { flags } = useFeatureFlags();
  const [newMessageCount, setNewMessageCount] = useState(0);

  // Officer/admin role gives the *potential* to view the admin
  // board, but the feature flag has the final say.
  const canViewAdminMessages =
    (isOfficer || isAdmin) && flags.officerMessagesEnabled;

  // Query for new messages
  // For officers/admins WITH the flag on: include both personal messages AND admin board messages (receiverId null)
  // For everyone else: only personal messages
  const messagesQuery = useMemo(() => {
    if (canViewAdminMessages) {
      // Officers/admins see personal messages OR admin board messages
      // Must use and() when combining multiple filters with or()
      return query(
        collection(firestore, 'messages'),
        and(
          where('status', '==', 'new'),
          or(
            where('receiverId', '==', userId),
            where('receiverId', '==', null)
          )
        )
      );
    } else {
      // Regular members only see their own messages
      return query(
        collection(firestore, 'messages'),
        where('receiverId', '==', userId),
        where('status', '==', 'new')
      );
    }
  }, [firestore, userId, canViewAdminMessages]);

  // Listen to new messages in real-time
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        setNewMessageCount(snapshot.size);
      },
      (error) => {
        console.error('Error listening to messages:', error);
      }
    );

    return () => unsubscribe();
  }, [messagesQuery, userId]);

  // Don't show FAB if no new messages
  if (newMessageCount === 0) return null;

  // Determine where to navigate based on user role
  const handleClick = () => {
    if (canViewAdminMessages) {
      router.push('/admin/messages');
    } else {
      router.push('/messages');
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Button
        onClick={handleClick}
        size="lg"
        className="h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all relative"
        title={`You have ${newMessageCount} new ${newMessageCount === 1 ? 'message' : 'messages'}`}
      >
        <MessageCircle className="h-6 w-6" />
        <Badge
          variant="destructive"
          className="absolute -top-1 -right-1 h-6 w-6 flex items-center justify-center p-0 rounded-full"
        >
          {newMessageCount > 99 ? '99+' : newMessageCount}
        </Badge>
      </Button>
    </div>
  );
}
